/**
 * Home Page - Landing page for Subscrypts Telegram Bot.
 *
 * Features:
 * - Dramatic centered hero with gradient text
 * - 3D cyber-grid background
 * - Open layout (no wrapping Card)
 * - Responsive mobile hamburger header
 * - Animated FAQ accordion
 * - SEO structured data
 */

import { useEffect, useState } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import { Layout, CyberGrid, InfoSidebar } from '../components';
import { INFO_CONTENT } from '../content/infoContent';

// Constants
const BOT_ADD_URL = 'https://t.me/SubscryptsBot?startgroup=setup';
const DOCS_URL = 'https://docs.subscrypts.com/telegram-bot';
const WEBSITE_URL = 'https://subscrypts.com';

const FAQ_DATA = [
  {
    question: 'What is the Subscrypts Telegram Bot?',
    answer: 'A managed SaaS bot that connects blockchain-based subscription plans on Arbitrum One to Telegram group access. Group owners create on-chain plans, and the bot automatically grants or revokes access based on real-time subscription status.',
  },
  {
    question: 'How does the Subscrypts Telegram Bot work?',
    answer: 'Admins add the bot to their group, create subscription plans via a web form, and configure the group. Members link their Ethereum wallet using a gasless signature, then subscribe on-chain. The bot monitors the smart contract and manages group access automatically.',
  },
  {
    question: 'What blockchain network does it use?',
    answer: 'Arbitrum One — a Layer 2 network on Ethereum (Chain ID 42161). Transactions are fast and cost approximately $0.01 in ETH gas fees.',
  },
  {
    question: 'What tokens can members pay with?',
    answer: 'Subscription payments are made in SUBS tokens. Admins can price plans in SUBS or USDC terms — the amount is set when creating the plan on-chain.',
  },
  {
    question: 'How do I add the bot to my Telegram group?',
    answer: 'Click "Add to Telegram" on this page and select your group. The bot will guide you through the setup process with inline commands.',
  },
  {
    question: 'Do members need to share personal information?',
    answer: 'No. Wallet linking uses Sign-In with Ethereum (SIWE) — a gasless cryptographic signature. No passwords, emails, or personal data are collected. The bot only verifies wallet ownership.',
  },
  {
    question: 'What happens when a subscription expires?',
    answer: "The bot's reconciliation engine periodically checks on-chain state. When a subscription's next payment date passes without renewal, the member is automatically removed from the premium group. Members can resubscribe at any time to regain access.",
  },
  {
    question: 'Is the Subscrypts Telegram Bot free to use?',
    answer: 'Adding the bot and setting up your group is free. The Subscrypts smart contract charges a small commission on subscription payments — configurable per plan by the merchant.',
  },
  {
    question: 'Can I set up the Subscrypts Telegram Bot without coding or technical experience?',
    answer: 'Yes. The entire setup is no-code. The bot guides you through each step via inline buttons, and the plan creation form is a standard web form that you sign with your wallet. No smart contract deployment or programming is required.',
  },
];

const STEPS = [
  {
    number: '1',
    title: 'Add the Bot',
    description: (
      <>
        Click <strong className="text-accent">Add to Telegram</strong> and select
        your group. The bot will send a welcome message with setup instructions.
      </>
    ),
    bullets: [
      <><strong className="text-white">Subscrypts Bot</strong> is added to your group</>,
      <>Admins receive <strong className="text-white">setup instructions</strong> via bot message</>,
      <>Use <strong className="text-white">/setup</strong> command to begin configuration</>,
    ],
  },
  {
    number: '2',
    title: 'Create Plans',
    description: (
      <>
        Click <strong className="text-accent">Create Subscription</strong> in the
        admin message. Fill in the form and sign with your wallet to deploy your
        subscription plans on-chain.
      </>
    ),
    bullets: [
      <>Name, Description & duration (e.g., monthly)</>,
      <>Price in <strong className="text-white">SUBS</strong> or <strong className="text-white">USDC</strong></>,
      <>Set referral bonus and other parameters</>,
    ],
  },
  {
    number: '3',
    title: 'For Members: Link Wallet',
    description: (
      <>
        Members link their <strong className="text-accent">Ethereum wallet</strong>{' '}
        using the bot&apos;s wallet link command. The bot opens a private page with a{' '}
        <strong>gasless signature</strong> flow.
      </>
    ),
    bullets: [
      <>User signs a message (no gas cost)</>,
      <>Wallet is linked to Telegram account</>,
      <>Confirmation appears in the chat</>,
    ],
  },
  {
    number: '4',
    title: 'For Members: Subscribe',
    description: (
      <>
        Members visit the subscription page from the group. Connect their wallet,
        confirm the on-chain transaction, and the bot manages access automatically.
      </>
    ),
    bullets: [
      <>Navigate to the subscription page</>,
      <>Choose a plan and subscribe on-chain</>,
      <>Bot syncs access automatically</>,
    ],
  },
];

const Home = () => {
  useEffect(() => { document.title = 'Subscrypts Telegram Bot \u2014 Blockchain Subscriptions for Telegram'; }, []);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const { switchNetwork } = useWeb3();

  const handleAddArbitrum = async () => {
    try {
      await switchNetwork();
    } catch (err) {
      console.error('Failed to switch network:', err);
    }
  };

  return (
    <Layout showHeader={false} showFooter={false}>
      <main className="min-h-screen relative">
        {/* 3D Grid Background — fixed to viewport */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <CyberGrid />
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Custom Header */}
          <header className="sticky top-0 z-50 h-20 bg-[rgba(15,23,42,0.6)] backdrop-blur-[16px] border-b border-white/5 px-4 sm:px-6 flex items-center">
            <div className="max-w-6xl mx-auto w-full flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="font-display font-bold text-xl text-white">Subscrypts</span>
              </div>

              {/* Desktop nav links */}
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={() => setShowInfo(true)}
                  className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors text-text-muted hover:text-white"
                  aria-label="Page info"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth="1.5" />
                    <path strokeLinecap="round" strokeWidth="2" d="M12 16v-4m0-4h.01" />
                  </svg>
                </button>
                <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer"
                  className="px-4 py-2 text-sm rounded-full text-text-muted hover:text-white hover:bg-white/5 border border-transparent transition-all">
                  Website
                </a>
                <a href={DOCS_URL} target="_blank" rel="noopener noreferrer"
                  className="px-4 py-2 text-sm rounded-full text-text-muted hover:text-white hover:bg-white/5 border border-transparent transition-all">
                  Docs
                </a>
                <a href={BOT_ADD_URL}
                  className="px-5 py-2 text-sm rounded-full bg-accent text-slate-950 font-medium hover:bg-accent-hover shadow-glow transition-all">
                  Add to Telegram
                </a>
              </div>

              {/* Mobile: CTA + hamburger */}
              <div className="flex md:hidden items-center gap-2">
                <a href={BOT_ADD_URL}
                  className="px-4 py-2 text-sm rounded-full bg-accent text-slate-950 font-medium hover:bg-accent-hover shadow-glow transition-all">
                  Add to Telegram
                </a>
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors" aria-label="Toggle menu">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {mobileMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {/* Mobile menu dropdown */}
            {mobileMenuOpen && (
              <div className="md:hidden absolute top-20 left-0 right-0 bg-[rgba(15,23,42,0.95)] backdrop-blur-[20px] border-b border-white/5 animate-fade-in">
                <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col gap-2">
                  <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer"
                    className="px-4 py-3 rounded-2xl text-sm text-text-muted hover:text-white hover:bg-white/5 transition-colors">
                    Website
                  </a>
                  <a href={DOCS_URL} target="_blank" rel="noopener noreferrer"
                    className="px-4 py-3 rounded-2xl text-sm text-text-muted hover:text-white hover:bg-white/5 transition-colors">
                    Docs
                  </a>
                </div>
              </div>
            )}
          </header>

          {/* Hero Section */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 md:pt-24 pb-16 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-sm text-accent mb-8">
              Managed SaaS — No Code Required
            </span>

            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Blockchain{' '}
              <span className="bg-gradient-to-r from-accent via-emerald-400 to-teal-300 bg-clip-text text-transparent">
                Subscriptions
              </span>
              <br />
              for Your Telegram
            </h1>

            <p className="text-text-secondary text-lg md:text-xl max-w-2xl mx-auto mb-10">
              Create on-chain subscription plans and manage Telegram group access automatically.
              Price plans in{' '}
              <strong className="text-accent">SUBS</strong> or{' '}
              <strong className="text-accent">USDC</strong> — powered by Arbitrum One.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
              <a href={BOT_ADD_URL}
                className="inline-flex items-center px-8 py-3 rounded-full bg-accent text-slate-950 font-bold font-display shadow-glow hover:shadow-glow-lg hover:bg-accent-hover transition-all text-lg">
                + Add Subscrypts to Telegram
              </a>
              <a href="#steps"
                className="inline-flex items-center px-6 py-3 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-all font-medium">
                See how it works
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3">
              <span className="inline-flex items-center px-3 py-1 text-xs rounded-full border border-white/10 bg-white/5 text-text-muted">
                Chain: Arbitrum One
              </span>
              <span className="inline-flex items-center px-3 py-1 text-xs rounded-full border border-white/10 bg-white/5 text-text-muted">
                Non-Custodial
              </span>
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-16" />
          </div>

          {/* Prerequisites Section */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-16">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">Prerequisites</h2>
            <p className="text-text-muted mb-8">Simple prep so setup is smooth.</p>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="glass-panel rounded-2xl p-6 hover:border-accent/20 transition-colors">
                <h3 className="font-display font-bold text-white mb-2">Telegram Group</h3>
                <p className="text-sm text-text-muted">
                  You need to be a group admin to add the bot and configure subscriptions.
                </p>
              </div>
              <div className="glass-panel rounded-2xl p-6 hover:border-accent/20 transition-colors">
                <h3 className="font-display font-bold text-white mb-2">Crypto Wallet Extension</h3>
                <p className="text-sm text-text-muted">
                  <a href="https://metamask.io/download" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                    Install MetaMask
                  </a>{' '}
                  or any Ethereum-compatible wallet and add{' '}
                  <button onClick={handleAddArbitrum} className="text-accent hover:underline">
                    Arbitrum One
                  </button>{' '}
                  network.
                </p>
              </div>
              <div className="glass-panel rounded-2xl p-6 hover:border-accent/20 transition-colors">
                <h3 className="font-display font-bold text-white mb-2">Fund Wallet with ETH</h3>
                <p className="text-sm text-text-muted">
                  Fund your wallet with ETH for paying transactions on Arbitrum. Estimated cost per subscription: ~$0.01.
                </p>
              </div>
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-16" />
          </div>

          {/* Steps Section */}
          <section id="steps" className="max-w-6xl mx-auto px-4 sm:px-6 mb-16">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">Setup — Step by Step</h2>
            <p className="text-text-muted mb-10">Follow these steps to get your group live.</p>

            <div className="space-y-6">
              {STEPS.map((step) => (
                <div key={step.number} className="glass-panel rounded-2xl overflow-hidden">
                  <div className="p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-8 h-8 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center text-accent font-bold text-sm font-display">
                        {step.number}
                      </span>
                      <h3 className="font-display text-xl font-bold text-white">{step.title}</h3>
                    </div>
                    <p className="text-text-muted mb-4">{step.description}</p>
                    <ul className="text-sm text-text-muted space-y-1 list-disc list-inside">
                      {step.bullets.map((bullet, j) => (
                        <li key={j}>{bullet}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-16" />
          </div>

          {/* Quick Help Section */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-16">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-8">Quick Help</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="glass-panel rounded-2xl p-6 hover:border-accent/20 transition-colors">
                <h3 className="font-display font-bold text-white mb-2">Bot not responding?</h3>
                <p className="text-sm text-text-muted">
                  Make sure the bot has <em>admin permissions</em> in the group and is not muted.
                </p>
              </div>
              <div className="glass-panel rounded-2xl p-6 hover:border-accent/20 transition-colors">
                <h3 className="font-display font-bold text-white mb-2">Wrong network?</h3>
                <p className="text-sm text-text-muted">
                  Switch MetaMask to <strong>Arbitrum One</strong> (Chain ID 42161).
                </p>
              </div>
              <div className="glass-panel rounded-2xl p-6 hover:border-accent/20 transition-colors">
                <h3 className="font-display font-bold text-white mb-2">Out of gas?</h3>
                <p className="text-sm text-text-muted">
                  Add a little <strong>ETH on Arbitrum</strong>. Subscription payments use <strong>SUBS</strong> tokens.
                </p>
              </div>
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-16" />
          </div>

          {/* FAQ Section */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-16">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">Frequently Asked Questions</h2>
            <p className="text-text-muted mb-8">Common questions about the Subscrypts Telegram Bot.</p>

            <div className="space-y-3">
              {FAQ_DATA.map((faq, i) => (
                <div key={i} className="glass-panel rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="font-bold text-white pr-4">{faq.question}</span>
                    <span className={`text-text-muted text-xl shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-45' : ''}`}>
                      +
                    </span>
                  </button>
                  <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${openFaq === i ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                    <div className="overflow-hidden min-h-0">
                      <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-text-muted leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ JSON-LD */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: FAQ_DATA.map((faq) => ({
                  '@type': 'Question',
                  name: faq.question,
                  acceptedAnswer: { '@type': 'Answer', text: faq.answer },
                })),
              }),
            }}
          />

          {/* Footer */}
          <footer className="max-w-6xl mx-auto px-4 sm:px-6 pb-8">
            <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-white/5 text-sm text-text-muted">
              <span>&copy; {new Date().getFullYear()} Subscrypts - Telegram Bot</span>
              <div className="flex items-center gap-4">
                <a href={DOCS_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Docs</a>
                <a href={BOT_ADD_URL}
                  className="px-4 py-1.5 rounded-full bg-accent text-slate-950 font-medium hover:bg-accent-hover shadow-glow transition-all">
                  Add to Telegram
                </a>
              </div>
            </div>
          </footer>
        </div>
      </main>

      <InfoSidebar
        isOpen={showInfo}
        onClose={() => setShowInfo(false)}
        title={INFO_CONTENT.home.title}
        description={INFO_CONTENT.home.description}
        sections={INFO_CONTENT.home.sections}
      />
    </Layout>
  );
};

export default Home;
